/**
 * STAGE: long clips go back to WhisperX to be broken down.
 *
 * WhisperX groups connected speech, so a "cut" can be a whole phrase. Two ways
 * to break it down, offered as an A/B:
 *
 *   burst  — physically cut that span out and TRANSCRIBE it fresh
 *   guided — keep Gemini's words and only ask WhisperX to PLACE them
 *
 * PARTITION: children always tile the parent's span exactly. The seam loop at
 * the bottom of the per-tile block is what guarantees it — each child starts
 * where the previous one ended, the first takes the parent's start and the last
 * takes the parent's end — so the timeline stays fully covered whichever way it
 * goes, and a tile that cannot be broken down is simply left whole.
 *
 * The `./whisperxClient` import stays DYNAMIC on purpose (see `baseWhisperx`).
 */

import { cleanStoredLabel, isFillerSpelling } from '../../lib/labelText';
import { round3 } from '../../lib/num';
import { sliceToWav } from '../audioSlicer';
import type { AlignedTile, AlignedTilesResult } from './alignedTiling';

export interface RecutOptions {
  buffer: AudioBuffer;
  /** The tiling to re-cut. `tiles` is REPLACED in place when anything changed. */
  tiled: AlignedTilesResult;
  /** `true` = guided (place Gemini's words), `false` = burst (transcribe fresh). */
  guided: boolean;
  /**
   * Carried into the burst mode's fresh transcription. Without it the fresh
   * pass drops hesitations again and undoes the whole point of the filler modes.
   */
  biasFillers: boolean;
  signal?: AbortSignal;
  onPhase?: (message: string, frac: number) => void;
}

export async function recutLongCuts(options: RecutOptions): Promise<void> {
  const { buffer, tiled, guided, biasFillers, signal, onPhase } = options;
  onPhase?.(guided ? 'WhisperX: placing words in long cuts…' : 'WhisperX: re-cutting long clips…', 0.95);
  const wxc = await import('../whisperxClient');
  const longOnes = tiled.tiles.filter(
    (t) => t.kind !== 'silence' && t.heard.trim().split(/\s+/).filter(Boolean).length >= 2,
  );
  const replacements = new Map<AlignedTile, AlignedTile[]>();
  for (const tile of longOnes) {
    try {
      const clip = sliceToWav(buffer, tile.startSec, tile.endSec, { padSec: 0 });
      const words = guided
        ? await wxc.alignWithWhisperx({
            wav: clip,
            text: tile.heard,
            language: tiled.language,
            signal,
          })
        : (
            await wxc.transcribeWithWhisperx({
              wav: clip,
              // Without this the fresh transcription drops hesitations
              // again and undoes the whole point of the filler modes.
              fillerBias: biasFillers,
              signal,
            })
          ).words;
      if (words.length < 2) continue;
      // relative -> absolute, then tile the parent's span exactly
      const kids: AlignedTile[] = [];
      for (let i = 0; i < words.length; i++) {
        const w = words[i];
        const startAbs = i === 0 ? tile.startSec : tile.startSec + Math.max(0, w.start);
        const endAbs =
          i === words.length - 1
            ? tile.endSec
            : tile.startSec + Math.max(w.end, w.start + 0.02);
        const prev = kids[kids.length - 1];
        const start = prev ? Math.max(startAbs, prev.endSec) : startAbs;
        const end = Math.max(endAbs, start + 0.05);
        if (end - start < 0.04) continue;
        kids.push({
          startSec: round3(start),
          endSec: round3(Math.min(end, tile.endSec)),
          heard: cleanStoredLabel(w.text) || w.text,
          contextual: cleanStoredLabel(w.text) || w.text,
          confidence: tile.confidence,
          kind: isFillerSpelling(w.text) ? 'filler' : 'word',
          trace: [...(tile.trace ?? []), guided ? 'recut:guided' : 'recut:burst'],
        });
      }
      if (kids.length < 2) continue;
      // seal the seams so the children cover the parent with no holes
      for (let i = 1; i < kids.length; i++) kids[i].startSec = kids[i - 1].endSec;
      kids[0].startSec = tile.startSec;
      kids[kids.length - 1].endSec = tile.endSec;
      replacements.set(tile, kids);
    } catch {
      // leave the clip whole rather than lose it
    }
  }
  if (replacements.size > 0) {
    const rebuilt: AlignedTile[] = [];
    for (const t of tiled.tiles) {
      const kids = replacements.get(t);
      if (kids) rebuilt.push(...kids);
      else rebuilt.push(t);
    }
    tiled.tiles = rebuilt;
  }
}
